import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Megaphone, Plus, Send, Users, X } from 'lucide-react';
import { crmApi, type ApiRecord } from '../../lib/api';

const LEAD_STATUSES = ['new', 'attempted', 'contacted', 'interested', 'follow_up', 'future', 'not_interested', 'converted'] as const;
const INDUSTRIES = [
	'veterinarian', 'dentist', 'medical', 'manufacturing', 'insurance', 'technology',
	'retail', 'finance', 'education', 'real_estate', 'construction', 'hospitality', 'legal', 'other'
] as const;

export function CampaignsPage() {
	const [builderOpen, setBuilderOpen] = useState(false);
	const campaignsQuery = useQuery({ queryKey: ['campaigns'], queryFn: crmApi.marketingCampaigns });
	const campaigns = (campaignsQuery.data ?? []) as ApiRecord[];

	return (
		<div className="space-y-3">
			<header className="rounded-2xl border border-subtle bg-surface-2 px-4 py-3 shadow-panel">
				<div className="flex items-start justify-between gap-3">
					<div>
						<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Campaigns</p>
						<h1 className="mt-1 text-xl font-semibold text-primary">Email outreach via MailBear</h1>
						<p className="mt-0.5 text-xs text-muted">
							Build an audience from your contacts, pick a template, schedule or send. Replies route back into the Inbox.
						</p>
					</div>
					<button
						onClick={() => setBuilderOpen(true)}
						className="inline-flex items-center gap-1.5 btn-primary hover:opacity-90"
					>
						<Plus className="h-4 w-4" /> New campaign
					</button>
				</div>
			</header>

			<div className="rounded-2xl border border-subtle bg-surface-2 p-3 shadow-panel">
				{campaigns.length === 0 ? (
					<div className="flex items-center gap-3 rounded-xl border border-dashed border-subtle px-3 py-4 text-sm text-muted">
						<Megaphone className="h-4 w-4 text-muted" />
						No campaigns yet. Build your first one.
					</div>
				) : (
					<table className="min-w-full divide-y divide-subtle text-sm">
						<thead className="text-left text-xs text-muted">
							<tr>
								<th className="px-3 py-2">Name</th>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2">Recipients</th>
								<th className="px-3 py-2">Sent</th>
								<th className="px-3 py-2">Opened</th>
								<th className="px-3 py-2">Created</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-subtle">
							{campaigns.map((c) => (
								<tr key={String(c.id)}>
									<td className="px-3 py-2 text-primary">{String(c.name ?? '—')}</td>
									<td className="px-3 py-2"><StatusPill status={String(c.status ?? '')} /></td>
									<td className="px-3 py-2 text-secondary">{String(c.recipient_count ?? 0)}</td>
									<td className="px-3 py-2 text-secondary">{String(c.sent_count ?? 0)}</td>
									<td className="px-3 py-2 text-secondary">{String(c.opened_count ?? 0)}</td>
									<td className="px-3 py-2 text-[11px] text-muted">{String(c.created_at ?? '').slice(0, 10)}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>

			{builderOpen ? <CampaignBuilder onClose={() => setBuilderOpen(false)} /> : null}
		</div>
	);
}

function StatusPill({ status }: { status: string }) {
	const tone =
		status === 'sent' ? 'bg-emerald-500/20 text-emerald-200'
			: status === 'sending' ? 'bg-accent/15 text-accent'
			: status === 'scheduled' ? 'bg-violet-500/20 text-violet-200'
			: 'bg-surface-hover text-secondary';
	return <span className={`rounded-full px-2 py-0.5 text-[10px] ${tone}`}>{status || 'draft'}</span>;
}

function CampaignBuilder({ onClose }: { onClose: () => void }) {
	const queryClient = useQueryClient();
	const templatesQuery = useQuery({ queryKey: ['marketing-templates'], queryFn: crmApi.marketingTemplates });
	const templates = (templatesQuery.data ?? []) as ApiRecord[];

	const [name, setName] = useState('');
	const [fromName, setFromName] = useState('');
	const [fromEmail, setFromEmail] = useState('');
	const [replyTo, setReplyTo] = useState('');
	const [templateId, setTemplateId] = useState<string>('');
	const [leadStatuses, setLeadStatuses] = useState<string[]>(['new']);
	const [industries, setIndustries] = useState<string[]>([]);
	const [hasEmail] = useState(true);
	const [scheduledAt, setScheduledAt] = useState('');

	const filters = {
		lead_statuses: leadStatuses.length ? leadStatuses : undefined,
		industry_types: industries.length ? industries : undefined,
		has_email: hasEmail
	};

	const previewQuery = useQuery({
		queryKey: ['audience-preview', leadStatuses.join(','), industries.join(',')],
		queryFn: () => crmApi.marketingPreviewAudience(filters),
		enabled: builderHasFilters(leadStatuses, industries)
	});

	const createMutation = useMutation({
		mutationFn: async (mode: 'draft' | 'send' | 'schedule') => {
			if (!name.trim()) throw new Error('Name is required');
			const created = await crmApi.marketingCreateCampaign({
				name: name.trim(),
				template_id: templateId ? Number(templateId) : null,
				from_name: fromName || null,
				from_email: fromEmail || null,
				reply_to: replyTo || null,
				audience_filters: filters
			});
			const id = Number((created as ApiRecord).id);
			await crmApi.marketingAddAudienceToCampaign(id, filters);
			if (mode === 'send') await crmApi.marketingSendCampaign(id);
			if (mode === 'schedule' && scheduledAt) {
				await crmApi.marketingScheduleCampaign(id, new Date(scheduledAt).toISOString());
			}
			return id;
		},
		onSuccess: (id, mode) => {
			toast.success(mode === 'send' ? 'Campaign sent' : mode === 'schedule' ? 'Campaign scheduled' : `Draft created (#${id})`);
			void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
			onClose();
		},
		onError: (e) => toast.error(e instanceof Error ? e.message : 'Create failed')
	});

	const count = previewQuery.data?.count ?? 0;

	const toggle = (list: string[], val: string, setter: (v: string[]) => void) => {
		setter(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);
	};

	return (
		<div className="fixed inset-0 z-50 flex items-stretch justify-end bg-canvas/70 backdrop-blur-sm">
			<aside className="flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l border-subtle bg-canvas p-5">
				<div className="flex items-start justify-between">
					<div>
						<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Campaign builder</p>
						<h2 className="mt-1 text-lg font-semibold text-primary">New email campaign</h2>
					</div>
					<button onClick={onClose} className="rounded-xl border border-subtle p-1.5 text-secondary hover:bg-surface-2">
						<X className="h-4 w-4" />
					</button>
				</div>

				<section className="mt-4 grid gap-2 rounded-xl border border-subtle bg-surface-2 p-3 sm:grid-cols-2">
					<Field label="Campaign name *"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></Field>
					<Field label="Template">
						<select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={inputCls}>
							<option value="">— pick later —</option>
							{templates.map((t) => <option key={String(t.id)} value={String(t.id)}>{String(t.name ?? `#${t.id}`)}</option>)}
						</select>
					</Field>
					<Field label="From name"><input value={fromName} onChange={(e) => setFromName(e.target.value)} className={inputCls} /></Field>
					<Field label="From email"><input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} className={inputCls} placeholder="sales@yourdomain.com" /></Field>
					<Field label="Reply-to"><input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} className={inputCls} /></Field>
				</section>

				<section className="mt-3 rounded-xl border border-subtle bg-surface-2 p-3">
					<p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Audience: lead status</p>
					<div className="mt-1.5 flex flex-wrap gap-1.5">
						{LEAD_STATUSES.map((s) => (
							<button key={s} onClick={() => toggle(leadStatuses, s, setLeadStatuses)} className={chipCls(leadStatuses.includes(s))}>
								{s.replace('_', ' ')}
							</button>
						))}
					</div>
					<p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-muted">Audience: industry</p>
					<div className="mt-1.5 flex flex-wrap gap-1.5">
						{INDUSTRIES.map((s) => (
							<button key={s} onClick={() => toggle(industries, s, setIndustries)} className={chipCls(industries.includes(s))}>
								{s.replace('_', ' ')}
							</button>
						))}
					</div>
					<div className="mt-3 flex items-center gap-2 rounded-lg bg-accent/10 px-3 py-2">
						<Users className="h-4 w-4 text-accent" />
						<p className="text-sm text-accent">
							{previewQuery.isFetching ? 'Counting…' : <><span className="font-semibold">{count}</span> matching contacts with email</>}
						</p>
					</div>
				</section>

				<section className="mt-3 rounded-xl border border-subtle bg-surface-2 p-3">
					<Field label="Schedule (leave empty to save as draft)">
						<input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className={inputCls} />
					</Field>
				</section>

				<div className="mt-4 flex flex-wrap justify-end gap-2">
					<button onClick={() => createMutation.mutate('draft')} disabled={createMutation.isPending} className="rounded-xl border border-subtle bg-surface-2 px-3 py-2 text-sm text-secondary hover:bg-surface-hover">
						Save draft
					</button>
					<button onClick={() => createMutation.mutate('schedule')} disabled={!scheduledAt || createMutation.isPending} className="rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-2 text-sm text-violet-200 hover:bg-violet-500/20 disabled:opacity-40">
						Schedule send
					</button>
					<button onClick={() => createMutation.mutate('send')} disabled={!templateId || count === 0 || createMutation.isPending} className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-500 px-3 py-2 text-sm font-semibold text-accent-contrast hover:bg-emerald-400 disabled:opacity-40">
						<Send className="h-4 w-4" /> Send now
					</button>
				</div>
			</aside>
		</div>
	);
}

const inputCls = 'w-full rounded-lg border border-subtle bg-canvas px-2.5 py-1.5 text-sm text-primary placeholder:text-muted';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<label className="block text-xs text-muted">
			<span className="block pb-1">{label}</span>
			{children}
		</label>
	);
}

function chipCls(active: boolean) {
	return active
		? 'rounded-lg bg-white px-2.5 py-1 text-[11px] font-medium text-accent-contrast shadow-panel'
		: 'rounded-lg border border-subtle bg-canvas/40 px-2.5 py-1 text-[11px] text-secondary hover:bg-surface-2';
}

function builderHasFilters(leadStatuses: string[], industries: string[]) {
	return leadStatuses.length > 0 || industries.length > 0;
}
